<template>
  <div class="document-type-grid">
    <div class="grid-header">
      <h6 class="grid-title">สร้างเอกสาร</h6>
    </div>

    <!-- Compact single-row layout -->
    <div class="document-actions">
      <!-- Medical Certificates -->
      <div class="action-group" v-if="medicalCertificates.length > 0">
        <span class="group-label">ใบรับรอง</span>
        <div class="action-chips">
          <DocumentTypeCard
            v-for="item in medicalCertificates" 
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :disabled="item.disabled"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
          />
        </div>
      </div>

      <!-- Consent Forms -->
      <div class="action-group" v-if="consentForms.length > 0">
        <span class="group-label">ใบยินยอม</span>
        <div class="action-chips">
          <DocumentTypeCard
            v-for="item in consentForms" 
            :key="item.value"
            :title="item.text"
            :value="item.value"
            :disabled="item.disabled"
            :is-generating="generatingDocument === item.value"
            @generate="handleGenerate"
          />
        </div>
      </div>

      <!-- Visual Separator -->
      <div v-if="templateDocuments.length > 0 || showTemplateUpload" class="section-divider"></div>

      <!-- Branch Template Documents -->
      <div class="action-group template-section" v-if="templateDocuments.length > 0 || showTemplateUpload">
        <span class="group-label">แบบฟอร์มคลินิก</span>
        <div class="template-body">
          <!-- ค้นหา — โผล่เมื่อแบบฟอร์มเยอะจนไล่สายตาหาไม่ไหว -->
          <div v-if="showTemplateFilter" class="template-search">
            <i class="fas fa-search template-search-icon"></i>
            <input
              v-model="templateFilter"
              type="search"
              class="template-search-input"
              placeholder="ค้นหาแบบฟอร์ม..."
              aria-label="ค้นหาแบบฟอร์มคลินิก"
            />
          </div>

          <div class="action-chips">
            <!-- Upload New Template Button — วางหน้าสุดให้กดได้เสมอ ไม่ว่าจะมีแบบฟอร์มกี่อัน -->
            <DocumentTypeCard
              v-if="showTemplateUpload"
              title="อัปโหลด PDF แบบฟอร์ม"
              value="upload_template"
              :disabled="false"
              :is-generating="false"
              :is-upload="true"
              @generate="handleTemplateUpload"
            />

            <!-- Existing Template Documents -->
            <DocumentTypeCard
              v-for="template in visibleTemplates"
              :key="template.id"
              :title="getTemplateName(template)"
              :value="template.id"
              :disabled="false"
              :is-generating="signingTemplate === template.id"
              :deletable="canManageClinicForm"
              @generate="handleTemplateSign"
              @delete="handleTemplateDelete"
            />

            <button
              v-if="canToggleTemplates"
              type="button"
              class="template-toggle"
              @click="showAllTemplates = !showAllTemplates"
            >
              <i
                :class="
                  showAllTemplates ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
              ></i>
              {{
                showAllTemplates
                  ? "ย่อ"
                  : `ดูทั้งหมด (${filteredTemplates.length})`
              }}
            </button>

            <span v-if="hasNoTemplateMatch" class="template-empty">
              ไม่พบแบบฟอร์มที่ค้นหา
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentTypeCard from './DocumentTypeCard.vue';
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";

// เกินเท่านี้ค่อยโชว์ช่องค้นหา — สาขาที่มีไม่กี่แบบฟอร์มไม่ต้องเจอ UI เพิ่ม
const TEMPLATE_FILTER_THRESHOLD = 8;
// จำนวน chip ที่โชว์ตอนย่อ ก่อนต้องกด "ดูทั้งหมด"
const TEMPLATE_COLLAPSED_COUNT = 12;

export default {
  name: "DocumentTypeGrid",
  mixins: [ClinicFormPermissionMixin],
  components: {
    DocumentTypeCard,
  },
  props: {
    documentTypes: {
      type: Array,
      default: () => [],
    },
    generatingDocument: {
      type: String,
      default: null,
    },
    templateDocuments: {
      type: Array,
      default: () => [],
    },
    // mediaFile id ของแบบฟอร์มเป็น Number ส่วน key ของเอกสารที่ generate เป็น String
    signingTemplate: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      templateFilter: "",
      showAllTemplates: false,
    };
  },
  computed: {
    medicalCertificates() {
      return this.flattenedDocuments.filter(item => 
        ['medicalCert', 'referralLetter'].includes(item.value) && !item.disabled
      );
    },
    consentForms() {
      return this.flattenedDocuments.filter(item => 
        item.value.includes('Consent') && !item.disabled
      );
    },
    flattenedDocuments() {
      const flattened = [];
      this.documentTypes.forEach(item => {
        if (item.options) {
          // Handle grouped options (consent forms)
          item.options.forEach(option => {
            flattened.push(option);
          });
        } else if (item.value !== 'empty') {
          // Handle regular items (exclude the disabled placeholder)
          flattened.push(item);
        }
      });
      return flattened;
    },
    showTemplateUpload() {
      // เพิ่มแบบฟอร์มได้เฉพาะผู้จัดการสาขา / RDM / C_LEVEL
      return this.canManageClinicForm;
    },
    showTemplateFilter() {
      return this.templateDocuments.length > TEMPLATE_FILTER_THRESHOLD;
    },
    normalizedTemplateFilter() {
      return this.templateFilter.trim().toLowerCase();
    },
    isTemplateFiltering() {
      return this.normalizedTemplateFilter.length > 0;
    },
    filteredTemplates() {
      if (!this.isTemplateFiltering) return this.templateDocuments;
      const keyword = this.normalizedTemplateFilter;
      return this.templateDocuments.filter(template =>
        this.getTemplateName(template)
          .toLowerCase()
          .includes(keyword)
      );
    },
    // ตอนค้นหาให้โชว์ผลทั้งหมด — คำค้นย่อรายการให้อยู่แล้ว ไม่ต้องย่อซ้ำ
    visibleTemplates() {
      if (this.isTemplateFiltering || this.showAllTemplates) {
        return this.filteredTemplates;
      }
      return this.filteredTemplates.slice(0, TEMPLATE_COLLAPSED_COUNT);
    },
    canToggleTemplates() {
      return (
        !this.isTemplateFiltering &&
        this.filteredTemplates.length > TEMPLATE_COLLAPSED_COUNT
      );
    },
    hasNoTemplateMatch() {
      return this.isTemplateFiltering && this.filteredTemplates.length === 0;
    },
  },
  methods: {
    getTemplateName(template) {
      return template.name || template.remark || "";
    },
    handleGenerate(documentType) {
      this.$emit('generate', documentType);
    },
    handleTemplateSign(templateId) {
      this.$emit('template-sign', templateId);
    },
    handleTemplateUpload() {
      this.$emit('template-upload');
    },
    handleTemplateDelete(templateId) {
      this.$emit('template-delete', templateId);
    },
    getDocumentDescription(type) {
      const descriptions = {
        medicalCert: 'ใบรับรองทันตแพทย์สำหรับการลา หรือการแสดงหลักฐาน',
        referralLetter: 'หนังสือส่งตัวผู้ป่วยไปยังแพทย์เฉพาะทาง',
        dentalImplantConsent: 'ใบยินยอมสำหรับการรักษาด้วยรากเทียม',
        veneerConsent: 'ใบยินยอมสำหรับการติดวีเนียร์',
        traditionalBracesConsent: 'ใบยินยอมสำหรับการจัดฟันแบบดั้งเดิม',
        clearAlignerConsent: 'ใบยินยอมสำหรับการจัดฟันใส',
        marketingConsent: 'ใบยินยอมการใช้ข้อมูลเพื่อการตลาด',
      };
      return descriptions[type] || '';
    },
    getDocumentIcon(type) {
      const icons = {
        medicalCert: 'fas fa-file-medical',
        referralLetter: 'fas fa-file-export',
        dentalImplantConsent: 'fas fa-tooth',
        veneerConsent: 'fas fa-smile',
        traditionalBracesConsent: 'fas fa-teeth',
        clearAlignerConsent: 'fas fa-teeth-open',
        marketingConsent: 'fas fa-bullhorn',
      };
      return icons[type] || 'fas fa-file-alt';
    },
  },
  emits: ['generate', 'template-sign', 'template-upload', 'template-delete'],
};
</script>

<style scoped>
.document-type-grid {
  padding: 0;
}

.grid-header {
  text-align: center;
  margin-bottom: 1rem;
}

.grid-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0;
  font-size: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  display: inline-block;
}

.document-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}

.group-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 100px;
  text-align: right;
  flex-shrink: 0;
}

.action-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
}

.section-divider {
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, transparent 0%, #dee2e6 20%, #dee2e6 80%, transparent 100%);
  margin: 0.75rem 0;
  position: relative;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 50%;
}

.template-section {
  margin-top: 0.5rem;
}

.template-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.template-search {
  position: relative;
  max-width: 260px;
}

.template-search-icon {
  position: absolute;
  left: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.75rem;
  pointer-events: none;
}

.template-search-input {
  width: 100%;
  padding: 0.3rem 0.5rem 0.3rem 1.85rem;
  font-size: 0.8rem;
  color: #2c3e50;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.template-search-input:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
}

.template-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 500;
  color: #6c757d;
  background: transparent;
  border: 1px dashed #ced4da;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.template-toggle:hover {
  color: #495057;
  border-color: #adb5bd;
}

.template-empty {
  font-size: 0.8rem;
  color: #adb5bd;
  padding: 0.3rem 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid-title {
    font-size: 0.9rem;
  }
  
  .document-actions {
    gap: 0.75rem;
  }
  
  .action-group {
    gap: 0.5rem;
  }
  
  .group-label {
    font-size: 0.7rem;
    min-width: 80px;
  }
  
  .action-chips {
    gap: 0.375rem;
  }
}

@media (max-width: 576px) {
  .grid-title {
    font-size: 0.85rem;
  }
  
  .action-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
  }
  
  .group-label {
    text-align: left;
    min-width: auto;
    font-size: 0.65rem;
  }
  
  .action-chips {
    gap: 0.25rem;
  }

  .template-search {
    max-width: 100%;
  }
}
</style>